import { Block, ForwardToInbox } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { resendInvitation, revokeInvitation } from "./referralPartnersApi";

const RESEND = "resend";
const CANCEL = "cancel";

const errorText = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

/** Resend and cancel for the partner on this row; the confirmation itself lives with the page. */
export const InvitationRowActions = ({ partner, onAsk }) => {
  if (!partner?.invitation) return null;
  const ask = (kind) => (event) => {
    event.stopPropagation();
    onAsk(kind, partner);
  };
  return (
    <div className="flex items-center gap-1">
      <Tooltip title="Resend invitation">
        <IconButton
          size="small"
          color="primary"
          aria-label="Resend invitation"
          onClick={ask(RESEND)}
        >
          <ForwardToInbox fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Cancel invitation">
        <IconButton size="small" color="error" aria-label="Cancel invitation" onClick={ask(CANCEL)}>
          <Block fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  );
};

const copyFor = (kind, partner) => {
  const name = partner?.displayName || "this partner";
  if (kind === RESEND) {
    return {
      title: "Resend invitation?",
      message: `We’ll email a new onboarding link to ${partner?.email}. The link ${name} already has will stop working.`,
      confirmLabel: "Resend invitation",
      confirmColor: "primary",
      cancelLabel: "Not now",
    };
  }
  const staysListed = (partner?.clientCount || 0) > 0;
  return {
    title: "Cancel invitation?",
    message: staysListed
      ? `${name} won’t be able to sign up with the link they were sent. They already have referred clients, so they stay in the list as not enrolled yet.`
      : `${name} won’t be able to sign up with the link they were sent, and will disappear from this list. You can invite them again later.`,
    confirmLabel: "Cancel invitation",
    confirmColor: "error",
    cancelLabel: "Keep invitation",
  };
};

/**
 * Owns the confirm-then-call flow for both actions. The returned `dialog` has to render outside
 * the table: a click inside a portal still bubbles to the row and would open the partner.
 */
export const useInvitationActions = (onChanged) => {
  const [target, setTarget] = useState(null);
  const close = () => setTarget(null);

  const mutation = useMutation(
    ({ kind, partner }) =>
      kind === RESEND
        ? resendInvitation(partner.invitation.id)
        : revokeInvitation(partner.invitation.id),
    {
      onSuccess: (_result, { kind, partner }) => {
        toast.success(
          kind === RESEND
            ? `Invitation resent to ${partner.email}. The previous link no longer works.`
            : `Invitation to ${partner.displayName} cancelled`
        );
        close();
        onChanged?.();
      },
      onError: (err, { kind }) => {
        toast.error(
          errorText(
            err,
            kind === RESEND ? "Could not resend the invitation" : "Could not cancel the invitation"
          )
        );
        // The row may be stale (already cancelled or replaced elsewhere); show what is true now.
        close();
        onChanged?.();
      },
    }
  );

  const copy = copyFor(target?.kind, target?.partner);
  const dialog = (
    <ConfirmDialog
      open={Boolean(target)}
      title={copy.title}
      message={copy.message}
      confirmLabel={copy.confirmLabel}
      confirmColor={copy.confirmColor}
      cancelLabel={copy.cancelLabel}
      loading={mutation.isLoading}
      onConfirm={() => mutation.mutate(target)}
      onCancel={close}
    />
  );

  const ask = useCallback((kind, partner) => setTarget({ kind, partner }), []);

  return { ask, dialog };
};
